import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../utils/apiClient';
import { apiErrorMessage } from '../../services/agentApi';
import { btn, input, Spinner, fmtDateTime, RunStatusChip, EmailStatusChip, REJECT_REASONS, card } from '../agent/agentUi';
import RunTimeline from '../agent/RunTimeline';
import type { AgentRun, AgentRunDetail, InboundEmail, CompanyAgentSettings } from '../../types/agent';

type Overview = {
  config: Record<string, any>;
  killSwitch: { platformEnabled: boolean };
  poller: { replicaId: string; running: boolean; lastTickAt: string | null; lastError: string | null; lastIngested: number };
  mailboxes: Array<{ id: string; address: string; label: string | null; companyId: string | null; status: string; transport: string; lastPolledAt: string | null; lastPollError: string | null; transportState: { hasDeltaLink: boolean; deltaUpdatedAt: string | null } }>;
  last24h: { emails: Record<string, number>; runs: Record<string, number> };
  last7d: { emails: Record<string, number>; runs: Record<string, number> };
  stuckRuns: number;
  pendingApprovals: number;
  inboundLagSeconds: number;
  warnings: string[];
};

const sum = (o: Record<string, number>) => Object.values(o || {}).reduce((a, b) => a + b, 0);

const Tile: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({ label, value, tone }) => (
  <div className={`${card} p-3`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-xl font-semibold ${tone || 'text-gray-900 dark:text-white'}`}>{value}</div>
  </div>
);

/** One-click "Handle email sent to the agent mailbox" for a company. Saves on change. */
const CompanyEmailToggle: React.FC<{ companyId: string; enabled: boolean; label: string; onChanged: () => void }> = ({ companyId, enabled, label, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const change = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.put(`/api/sysadmin/agent/settings/${companyId}`, { enabled: next });
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="inline-flex items-center gap-1.5 cursor-pointer">
        <input type="checkbox" checked={enabled} disabled={busy} onChange={(e) => change(e.target.checked)} />
        <span>{label}</span>
      </label>
      {error && <p className="text-red-600 dark:text-red-400 mt-0.5">{error}</p>}
    </div>
  );
};

const OverviewTab: React.FC = () => {
  const [o, setO] = useState<Overview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newMailbox, setNewMailbox] = useState({ companyId: '', address: '', label: '' });
  const [editing, setEditing] = useState<{ id: string; companyId: string; address: string } | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; message: string } | 'testing'>>({});
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string; settings: CompanyAgentSettings | null }>>([]);

  const load = useCallback(() => {
    apiClient.get<Overview>('/api/sysadmin/agent/overview').then(setO).catch((err) => setError(apiErrorMessage(err)));
    apiClient.get<{ companies: Array<{ id: string; name: string; settings: CompanyAgentSettings | null }> }>('/api/sysadmin/agent/companies').then((r) => setCompanies(r.companies)).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const kill = async (enabled: boolean) => {
    setBusy(true);
    await apiClient.post('/api/sysadmin/agent/kill-switch', { enabled }).catch((err) => setError(apiErrorMessage(err)));
    setBusy(false);
    load();
  };
  const patchMailbox = async (id: string, body: Record<string, any>) => {
    setError(null);
    try {
      await apiClient.patch(`/api/sysadmin/agent/mailboxes/${id}`, body);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update the mailbox'));
      return false;
    } finally {
      load();
    }
  };
  const testMailbox = async (id: string) => {
    setTestResults((r) => ({ ...r, [id]: 'testing' }));
    try {
      const result = await apiClient.post<{ ok: boolean; message: string }>(`/api/sysadmin/agent/mailboxes/${id}/test`);
      setTestResults((r) => ({ ...r, [id]: result }));
    } catch (err) {
      setTestResults((r) => ({ ...r, [id]: { ok: false, message: apiErrorMessage(err, 'Test failed') } }));
    }
  };
  const removeMailbox = async (id: string) => {
    setConfirmRemove(null);
    setError(null);
    try {
      const r = await apiClient.delete<{ removed: boolean; message?: string }>(`/api/sysadmin/agent/mailboxes/${id}`);
      if (r?.message) setNotice(r.message);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove the mailbox'));
    }
    load();
  };
  const saveEdit = async () => {
    if (!editing) return;
    const ok = await patchMailbox(editing.id, { companyId: editing.companyId || null, address: editing.address });
    if (ok) setEditing(null);
  };
  const createMailbox = async () => {
    setError(null);
    try {
      const r = await apiClient.post<{ mailbox: { id: string } }>('/api/sysadmin/agent/mailboxes', {
        companyId: newMailbox.companyId || null,
        address: newMailbox.address || undefined,
        label: newMailbox.label || undefined,
      });
      setNewMailbox({ companyId: '', address: '', label: '' });
      load();
      if (r?.mailbox?.id && o?.config.transport === 'graph') testMailbox(r.mailbox.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create the mailbox'));
    }
  };

  if (!o) return <div className="flex items-center gap-2 text-gray-500">{error ? <span className="text-red-600 dark:text-red-400">{error}</span> : <><Spinner /> Loading…</>}</div>;
  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p className="text-sm text-gray-700 dark:text-gray-200">{notice}</p>}
      {o.warnings.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          <ul className="list-disc ml-5">{o.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className={`px-3 py-1.5 rounded-md text-sm font-medium ${o.killSwitch.platformEnabled && o.config.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'}`}>
          Agent {o.killSwitch.platformEnabled && o.config.enabled ? 'ON' : 'OFF'} platform-wide{!o.config.enabled ? ' (AGENT_MAILBOX_ENABLED=false)' : ''}
        </div>
        {o.killSwitch.platformEnabled ? (
          <button className={btn.danger} disabled={busy} onClick={() => kill(false)}>Kill switch: stop all polling and runs</button>
        ) : (
          <button className={btn.success} disabled={busy} onClick={() => kill(true)}>Re-enable</button>
        )}
        <button className={btn.secondary} onClick={() => apiClient.post('/api/sysadmin/agent/poll-now').then(load).catch((err) => setError(apiErrorMessage(err)))}>Poll now</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile label="Emails (24h)" value={sum(o.last24h.emails)} />
        <Tile label="Runs (24h)" value={sum(o.last24h.runs)} />
        <Tile label="Failed runs (7d)" value={o.last7d.runs.failed || 0} tone={(o.last7d.runs.failed || 0) > 0 ? 'text-red-600' : undefined} />
        <Tile label="Quarantined (7d)" value={o.last7d.emails.quarantined || 0} />
        <Tile label="Pending approvals" value={o.pendingApprovals} />
        <Tile label="Inbound lag" value={`${o.inboundLagSeconds}s`} tone={o.inboundLagSeconds > 600 ? 'text-red-600' : undefined} />
      </div>
      <div className={`${card} p-3 text-xs text-gray-700 dark:text-gray-200 grid grid-cols-2 md:grid-cols-4 gap-2`}>
        <div>Transport: <strong>{o.config.transport}</strong>{o.config.transport === 'graph' ? (o.config.graphConfigured ? ' (credentials set)' : ' (credentials MISSING)') : ''}</div>
        <div>Mailbox: <strong>{o.config.mailboxUpn || '—'}</strong></div>
        <div>Poll every {o.config.pollSeconds}s · move processed: {String(o.config.moveProcessed)}</div>
        <div>Secret expires in: {o.config.secretDaysLeft ?? '—'} days</div>
        <div>This replica: {o.poller.replicaId}</div>
        <div>Poller: {o.poller.running ? 'running' : 'off'} · last tick {fmtDateTime(o.poller.lastTickAt)}</div>
        <div>Budgets: {o.config.maxIterations} iterations / {o.config.maxActiveMinutes} min</div>
        <div>Raw retention: {o.config.retentionDays} days</div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Mailboxes</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Give a company its own Arie address: its users email that address, and Arie replies from it. A company mailbox only accepts mail from that company's users.
        </p>
        <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-900 dark:text-gray-100">
          <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <tr><th className="text-left py-1.5">Address</th><th className="text-left py-1.5">Company</th><th className="text-left py-1.5">Transport</th><th className="text-left py-1.5">Status</th><th className="text-left py-1.5">Last poll</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {o.mailboxes.map((m) => {
              const isPlatform = !!o.config.mailboxUpn && m.address.toLowerCase() === String(o.config.mailboxUpn).toLowerCase();
              const test = testResults[m.id];
              const isEditing = editing?.id === m.id;
              return (
                <React.Fragment key={m.id}>
                  <tr>
                    <td className="py-1.5 font-mono text-xs">
                      {isEditing && !isPlatform ? (
                        <input className={input} value={editing!.address} onChange={(e) => setEditing({ ...editing!, address: e.target.value })} />
                      ) : (
                        m.address
                      )}
                    </td>
                    <td className="py-1.5 text-xs">
                      {isEditing ? (
                        <select className={input} value={editing!.companyId} onChange={(e) => setEditing({ ...editing!, companyId: e.target.value })}>
                          <option value="">Platform (any company)</option>
                          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      ) : m.companyId ? (
                        <>
                          <div>{companies.find((c) => c.id === m.companyId)?.name || 'company'}</div>
                          {companies.some((c) => c.id === m.companyId) && (
                            <CompanyEmailToggle
                              companyId={m.companyId}
                              enabled={!!companies.find((c) => c.id === m.companyId)?.settings?.enabled}
                              label="Handle email sent to the agent mailbox"
                              onChanged={load}
                            />
                          )}
                        </>
                      ) : (
                        <span className="text-gray-500">Platform (any company)</span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs">{m.transport}{m.transportState.hasDeltaLink ? ' · synced' : ''}</td>
                    <td className="py-1.5 text-xs">{m.status}</td>
                    <td className="py-1.5 text-xs">{fmtDateTime(m.lastPolledAt)}{m.lastPollError ? <span className="text-red-600 dark:text-red-400" title={m.lastPollError}> · error</span> : ''}</td>
                    <td className="py-1.5 text-right space-x-2 text-xs whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button className="text-primary hover:underline" onClick={saveEdit}>Save</button>
                          <button className="text-gray-500 hover:underline" onClick={() => setEditing(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {m.transport === 'graph' && (
                            <button className="text-primary hover:underline" disabled={test === 'testing'} onClick={() => testMailbox(m.id)}>
                              {test === 'testing' ? 'Testing…' : 'Test access'}
                            </button>
                          )}
                          <button className="text-primary hover:underline" onClick={() => setEditing({ id: m.id, companyId: m.companyId || '', address: m.address })}>Edit</button>
                          {m.status === 'active' ? <button className="text-amber-700 dark:text-amber-300 hover:underline" onClick={() => patchMailbox(m.id, { status: 'paused' })}>Pause</button> : <button className="text-green-700 dark:text-green-400 hover:underline" onClick={() => patchMailbox(m.id, { status: 'active' })}>Resume</button>}
                          {m.transport === 'graph' && <button className="text-gray-600 dark:text-gray-300 hover:underline" onClick={() => patchMailbox(m.id, { resetDelta: true })}>Re-sync</button>}
                          {!isPlatform && (confirmRemove === m.id ? (
                            <>
                              <button className="text-red-600 dark:text-red-400 font-semibold hover:underline" onClick={() => removeMailbox(m.id)}>Confirm remove</button>
                              <button className="text-gray-500 hover:underline" onClick={() => setConfirmRemove(null)}>Keep</button>
                            </>
                          ) : (
                            <button className="text-red-600 dark:text-red-400 hover:underline" onClick={() => setConfirmRemove(m.id)}>Remove</button>
                          ))}
                        </>
                      )}
                    </td>
                  </tr>
                  {test && test !== 'testing' && (
                    <tr>
                      <td colSpan={6} className={`pb-2 text-xs ${test.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {test.ok ? '✓ ' : '✕ '}{test.message}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="flex flex-wrap items-end gap-2 mt-3 text-xs">
          <label><span className="text-gray-500">Company</span>
            <select className={input} value={newMailbox.companyId} onChange={(e) => setNewMailbox({ ...newMailbox, companyId: e.target.value })}>
              <option value="">Platform (any company)</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="flex-1 min-w-[16rem]"><span className="text-gray-500">Mailbox address (e.g. acme@riskninja.ai)</span><input className={input} value={newMailbox.address} placeholder="name@domain" onChange={(e) => setNewMailbox({ ...newMailbox, address: e.target.value })} /></label>
          <label className="min-w-[12rem]"><span className="text-gray-500">Label (optional)</span><input className={input} value={newMailbox.label} onChange={(e) => setNewMailbox({ ...newMailbox, label: e.target.value })} /></label>
          <button className={btn.primary} onClick={createMailbox} disabled={!newMailbox.address.trim()}>Add mailbox</button>
        </div>
        {o.config.transport === 'graph' ? (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Before adding: create the address as a shared mailbox in Microsoft 365 and add it to the <strong>RiskNinja Agent Mailboxes</strong> group (Add-DistributionGroupMember). The policy change can take up to an hour; Test access confirms it. Polling starts automatically once access works.
          </p>
        ) : (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">This environment's transport is <strong>{o.config.transport}</strong>, so new mailboxes are recorded but not polled here.</p>
        )}
      </div>
    </div>
  );
};

const InboxTab: React.FC = () => {
  const [emails, setEmails] = useState<InboundEmail[] | null>(null);
  const [status, setStatus] = useState('quarantined,rejected,failed');
  const [users, setUsers] = useState<Array<{ id: string; email: string; companyId?: string }>>([]);
  const [assign, setAssign] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    apiClient.get<{ emails: InboundEmail[] }>(`/api/sysadmin/agent/inbox?status=${status}`).then((r) => setEmails(r.emails)).catch((err) => setMsg(apiErrorMessage(err)));
  }, [status]);
  useEffect(load, [load]);
  useEffect(() => {
    apiClient.get<{ users: any[] }>('/api/sysadmin/users').then((r) => setUsers(r.users || [])).catch(() => setUsers([]));
  }, []);

  const act = async (path: string, body?: any, label?: string) => {
    setMsg(null);
    try {
      const r: any = await apiClient.post(path, body);
      setMsg(`${label || 'Done'}${r?.outcome ? ` — ${r.outcome}` : ''}${r?.runId ? ` (run ${r.runId.slice(0, 8)})` : ''}`);
      load();
    } catch (err) {
      setMsg(apiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs">
        {['quarantined,rejected,failed', 'quarantined', 'rejected', 'failed', 'discarded'].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-2.5 py-1 rounded-full border ${status === s ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{s.replace(/,/g, ' / ')}</button>
        ))}
      </div>
      {msg && <p className="text-xs text-gray-700 dark:text-gray-200">{msg}</p>}
      {!emails ? <Spinner /> : emails.length === 0 ? <p className="text-sm text-gray-500">Nothing here.</p> : (
        <table className="w-full text-xs">
          <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <tr><th className="text-left py-1.5">Received</th><th className="text-left py-1.5">From</th><th className="text-left py-1.5">Subject</th><th className="text-left py-1.5">Status / reason</th><th className="text-left py-1.5">Company</th><th className="text-left py-1.5">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {emails.map((e) => (
              <tr key={e.id}>
                <td className="py-1.5 whitespace-nowrap">{fmtDateTime(e.receivedAt)}</td>
                <td className="py-1.5 max-w-[12rem] truncate">{e.fromAddress}</td>
                <td className="py-1.5 max-w-[16rem] truncate">{e.subject}</td>
                <td className="py-1.5"><EmailStatusChip status={e.status} /> <span className="text-gray-500">{REJECT_REASONS[e.rejectReason || ''] || e.rejectReason}</span></td>
                <td className="py-1.5">{e.companyName || '—'}</td>
                <td className="py-1.5 space-y-1">
                  <div className="flex gap-1">
                    <select className="text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 max-w-[11rem]" value={assign[e.id] || ''} onChange={(ev) => setAssign({ ...assign, [e.id]: ev.target.value })}>
                      <option value="">Assign to…</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                    </select>
                    <button className="text-primary hover:underline disabled:text-gray-400" disabled={!assign[e.id]} onClick={() => act(`/api/sysadmin/agent/inbox/${e.id}/assign`, { userId: assign[e.id] }, 'Assigned')}>Go</button>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-primary hover:underline" onClick={() => act(`/api/sysadmin/agent/inbox/${e.id}/replay`, undefined, 'Replayed')}>Replay</button>
                    {e.status !== 'discarded' && <button className="text-gray-600 dark:text-gray-300 hover:underline" onClick={() => act(`/api/sysadmin/agent/inbox/${e.id}/discard`, undefined, 'Discarded')}>Discard</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RunsTab: React.FC = () => {
  const [runs, setRuns] = useState<AgentRun[] | null>(null);
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<AgentRunDetail | null>(null);

  useEffect(() => {
    apiClient.get<{ runs: AgentRun[] }>(`/api/sysadmin/agent/runs${status ? `?status=${status}` : ''}`).then((r) => setRuns(r.runs)).catch(() => setRuns([]));
  }, [status]);

  if (detail) {
    return (
      <div>
        <button className="text-sm text-primary hover:underline mb-3" onClick={() => setDetail(null)}>← All runs</button>
        <div className="flex items-center gap-2 mb-2"><RunStatusChip status={detail.run.status} mode={detail.run.mode} /><span className="text-sm text-gray-700 dark:text-gray-200">{detail.inboundEmail?.subject || detail.run.instructions}</span></div>
        {detail.run.summary && <p className="text-sm text-gray-700 dark:text-gray-200 mb-3 whitespace-pre-wrap">{detail.run.summary}</p>}
        <div className={`${card} p-4`}><RunTimeline steps={detail.steps} /></div>
        <p className="text-xs text-gray-500 mt-2">Read-only cross-tenant trace · {detail.artifacts.length} artifact(s) · {detail.approvals.length} approval(s)</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs">
        {['', 'running,pending', 'awaiting_approval', 'failed', 'completed,completed_with_errors'].map((s) => (
          <button key={s || 'all'} onClick={() => setStatus(s)} className={`px-2.5 py-1 rounded-full border ${status === s ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{s ? s.replace(/,/g, ' / ') : 'all'}</button>
        ))}
      </div>
      {!runs ? <Spinner /> : (
        <table className="w-full text-xs">
          <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <tr><th className="text-left py-1.5">Created</th><th className="text-left py-1.5">Company</th><th className="text-left py-1.5">Owner</th><th className="text-left py-1.5">Subject</th><th className="text-left py-1.5">Status</th><th className="text-left py-1.5">Tokens</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {runs.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40" onClick={() => apiClient.get<AgentRunDetail>(`/api/sysadmin/agent/runs/${r.id}`).then(setDetail)}>
                <td className="py-1.5 whitespace-nowrap">{fmtDateTime(r.createdAt)}</td>
                <td className="py-1.5">{r.companyName || '—'}</td>
                <td className="py-1.5">{r.ownerEmail}</td>
                <td className="py-1.5 max-w-[18rem] truncate">{r.subject || r.instructions}</td>
                <td className="py-1.5"><RunStatusChip status={r.status} mode={r.mode} /></td>
                <td className="py-1.5">{(r.tokenUsage?.tokensIn || 0) + (r.tokenUsage?.tokensOut || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const CompaniesTab: React.FC = () => {
  const [rows, setRows] = useState<Array<{ id: string; name: string; settings: CompanyAgentSettings | null }> | null>(null);
  const [editing, setEditing] = useState<{ id: string; s: Partial<CompanyAgentSettings> } | null>(null);
  const load = useCallback(() => {
    apiClient.get<{ companies: any[] }>('/api/sysadmin/agent/companies').then((r) => setRows(r.companies)).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);
  const save = async () => {
    if (!editing) return;
    // The email checkbox saves itself; don't send back a stale value from when Edit was opened.
    const { enabled: _enabled, ...changes } = editing.s;
    await apiClient.put(`/api/sysadmin/agent/settings/${editing.id}`, changes);
    setEditing(null);
    load();
  };
  if (!rows) return <Spinner />;
  return (
    <table className="w-full text-xs">
      <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
        <tr><th className="text-left py-1.5">Company</th><th className="text-left py-1.5">Handle email sent to the agent mailbox</th><th className="text-left py-1.5">Autonomy</th><th className="text-left py-1.5">Daily cap</th><th /></tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.map((c) => {
          const s = editing?.id === c.id ? editing.s : c.settings || {};
          const isEditing = editing?.id === c.id;
          return (
            <tr key={c.id}>
              <td className="py-1.5">{c.name}</td>
              <td className="py-1.5"><CompanyEmailToggle companyId={c.id} enabled={!!c.settings?.enabled} label={c.settings?.enabled ? 'On' : 'Off'} onChanged={load} /></td>
              <td className="py-1.5">{isEditing ? (
                <select value={s.autonomyLevel || 'shadow'} onChange={(e) => setEditing({ id: c.id, s: { ...s, autonomyLevel: e.target.value as any } })} className="border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <option value="shadow">shadow</option><option value="assisted">assisted</option><option value="full">full</option>
                </select>
              ) : s.autonomyLevel || 'shadow'}</td>
              <td className="py-1.5">{isEditing ? <input type="number" className="w-16 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" value={s.dailyRunCap ?? 50} onChange={(e) => setEditing({ id: c.id, s: { ...s, dailyRunCap: Number(e.target.value) } })} /> : s.dailyRunCap ?? 50}</td>
              <td className="py-1.5 text-right space-x-2">
                {isEditing ? (<><button className="text-primary hover:underline" onClick={save}>Save</button><button className="text-gray-500 hover:underline" onClick={() => setEditing(null)}>Cancel</button></>) : <button className="text-primary hover:underline" onClick={() => setEditing({ id: c.id, s: { ...(c.settings || {}) } })}>Edit</button>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/** Sysadmin Agent Mailbox panel (AGENT_MAILBOX_PLAN.md §8.3, §5.3). */
const AgentMailboxAdmin: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [tab, setTab] = useState<'overview' | 'inbox' | 'runs' | 'companies'>('overview');
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Agent Mailbox</h2>
            <div className="flex gap-1">
              {(['overview', 'inbox', 'runs', 'companies'] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`text-xs px-2.5 py-1 rounded-full border ${tab === t ? 'border-primary text-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>{t === 'inbox' ? 'Quarantine & rejected' : t === 'runs' ? 'All runs' : t[0].toUpperCase() + t.slice(1)}</button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'overview' && <OverviewTab />}
          {tab === 'inbox' && <InboxTab />}
          {tab === 'runs' && <RunsTab />}
          {tab === 'companies' && <CompaniesTab />}
        </div>
      </div>
    </div>
  );
};

export default AgentMailboxAdmin;
